import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import StatusIndicator from '../../../components/ui/StatusIndicator';

const InvoiceTable = ({
  invoices = [],
  selectedInvoices = [],
  onSelectionChange,
  onSelectAll,
  onClearSelection,
  sortConfig,
  onSort,
  onDebug, // optional: open debug modal for invoice
  onDownload, // optional: download PDF for invoice
  onPreviewRequest,
  className = ''
}) => {
  const navigate = useNavigate();
  const [hoveredRow, setHoveredRow] = useState(null);
  const [previewState, setPreviewState] = useState({
    id: null,
    url: null,
    status: 'idle',
    error: null
  });

  const handleRowClick = (invoice) => {
    navigate('/invoice-editor', { state: { invoiceId: invoice?.id } });
  };

  const handleCheckboxChange = (invoiceId, checked) => {
    if (checked) {
      onSelectionChange([...selectedInvoices, invoiceId]);
    } else {
      onSelectionChange(selectedInvoices?.filter(id => id !== invoiceId));
    }
  };

  const handleSelectAllChange = (checked) => {
    if (checked) {
      onSelectAll();
    } else {
      onClearSelection();
    }
  };

  const getSortIcon = (column) => {
    if (sortConfig?.key !== column) return 'ArrowUpDown';
    return sortConfig?.direction === 'asc' ? 'ArrowUp' : 'ArrowDown';
  };

  const formatCurrency = (amount, currency = 'GBP') => {
    const symbol = currency === 'EUR' ? '€' : '£';
    return `${symbol}${amount?.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const parsedDate = new Date(dateString);
    if (Number.isNaN(parsedDate?.getTime())) return '—';
    return parsedDate?.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 0.9) return 'text-success';
    if (confidence >= 0.7) return 'text-warning';
    return 'text-error';
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case 'reviewed':
        return { status: 'reviewed', variant: 'default' };
      case 'needs_review':
        return { status: 'needs_review', variant: 'default' };
      case 'exported':
        return { status: 'reviewed', variant: 'default' };
      default:
        return { status: 'processing', variant: 'default' };
    }
  };

  const allSelected = invoices?.length > 0 && selectedInvoices?.length === invoices?.length;
  const someSelected = selectedInvoices?.length > 0 && selectedInvoices?.length < invoices?.length;

  const handlePreviewEnter = async (invoice, event) => {
    event?.stopPropagation();
    if (!onPreviewRequest) return;
    setPreviewState(prev => ({
      id: invoice?.id,
      url: prev?.id === invoice?.id ? prev?.url : null,
      status: prev?.id === invoice?.id && prev?.url ? 'ready' : 'loading',
      error: null
    }));
    try {
      const url = await onPreviewRequest(invoice);
      setPreviewState({ id: invoice?.id, url, status: 'ready', error: null });
    } catch (err) {
      setPreviewState({
        id: invoice?.id,
        url: null,
        status: 'error',
        error: err?.message || 'Preview unavailable'
      });
    }
  };

  const handlePreviewLeave = (event) => {
    event?.stopPropagation();
    setPreviewState({ id: null, url: null, status: 'idle', error: null });
  };

  return (
    <div className={`bg-card border border-border rounded-lg overflow-hidden ${className}`}>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="w-12 p-4">
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={(e) => handleSelectAllChange(e?.target?.checked)}
                />
              </th>
              
              {[
                { key: 'vendor', label: 'Vendor', sortable: true },
                { key: 'invoiceNumber', label: 'Invoice #', sortable: true },
                { key: 'date', label: 'Date', sortable: true },
                { key: 'netAmount', label: 'Net Amount', sortable: true },
                { key: 'vatAmount', label: 'VAT', sortable: true },
                { key: 'totalAmount', label: 'Total', sortable: true },
                { key: 'confidence', label: 'Confidence', sortable: true },
                { key: 'status', label: 'Status', sortable: true },
                { key: 'actions', label: 'Actions', sortable: false }
              ]?.map((column) => (
                <th
                  key={column?.key}
                  className={`px-4 py-3 text-left text-sm font-medium text-muted-foreground ${
                    column?.sortable ? 'cursor-pointer hover:text-foreground' : ''
                  }`}
                  onClick={column?.sortable ? () => onSort(column?.key) : undefined}
                >
                  <div className="flex items-center space-x-2">
                    <span>{column?.label}</span>
                    {column?.sortable && (
                      <Icon 
                        name={getSortIcon(column?.key)} 
                        size={14}
                        className="opacity-50"
                      />
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          
          <tbody>
            {invoices?.map((invoice) => {
              const isSelected = selectedInvoices?.includes(invoice?.id);
              const isReviewed = invoice?.status === 'reviewed' || invoice?.status === 'exported';
              const statusConfig = getStatusConfig(invoice?.status);
              
              return (
                <tr
                  key={invoice?.id}
                  className={`
                    border-b border-border transition-smooth cursor-pointer
                    ${isReviewed ? 'bg-success-pale' : 'hover:bg-muted/30'}
                    ${isSelected ? 'bg-primary/5' : ''}
                    ${hoveredRow === invoice?.id ? 'shadow-sm' : ''}
                  `}
                  onMouseEnter={() => setHoveredRow(invoice?.id)}
                  onMouseLeave={() => setHoveredRow(null)}
                  onClick={() => handleRowClick(invoice)}
                >
                  <td className="p-4" onClick={(e) => e?.stopPropagation()}>
                    <Checkbox
                      checked={isSelected}
                      onChange={(e) => handleCheckboxChange(invoice?.id, e?.target?.checked)}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center space-x-3">
                      <div
                        className="relative"
                        onMouseEnter={(e) => handlePreviewEnter(invoice, e)}
                        onMouseLeave={handlePreviewLeave}
                      >
                        <div
                          className="w-8 h-8 bg-muted rounded-lg flex items-center justify-center"
                          onClick={(e) => e?.stopPropagation()}
                        >
                          <Icon name="FileText" size={16} className="text-muted-foreground" />
                        </div>
                        {previewState?.id === invoice?.id && (
                          <div className="fixed left-8 top-28 z-50 w-[520px] h-[640px] bg-card text-foreground border border-border rounded-xl shadow-2xl p-3 pointer-events-none">
                            {previewState?.status === 'loading' && (
                              <div className="w-full h-full flex items-center justify-center text-sm text-muted-foreground">
                                Loading preview…
                              </div>
                            )}
                            {previewState?.status === 'error' && (
                              <div className="w-full h-full flex items-center justify-center text-sm text-destructive">
                                {previewState?.error || 'Preview unavailable'}
                              </div>
                            )}
                            {previewState?.status === 'ready' && previewState?.url && (
                              (() => {
                                const ext = invoice?.filename?.split('.')?.pop()?.toLowerCase();
                                const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp']?.includes(ext);
                                return isImage ? (
                                  <img
                                    src={previewState?.url}
                                    alt="Invoice preview"
                                    className="w-full h-full object-contain rounded"
                                  />
                                ) : (
                                  <iframe
                                    title="Invoice preview"
                                    src={`${previewState?.url}#toolbar=0&view=FitH`}
                                    className="w-full h-full rounded"
                                  />
                                );
                              })()
                            )}
                          </div>
                        )}
                      </div>
                      <div>
                        <div className="font-medium text-foreground">{invoice?.vendor}</div>
                        <div className="text-xs text-muted-foreground">{invoice?.filename}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-mono text-sm text-foreground">{invoice?.invoiceNumber}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-foreground">{formatDate(invoice?.date)}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-mono text-sm text-foreground">
                      {formatCurrency(invoice?.netAmount, invoice?.currency)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col space-y-1">
                      {invoice?.vatBreakdown?.map((vat, index) => (
                        <div key={index} className="flex items-center space-x-2">
                          <span className="text-xs bg-muted px-1.5 py-0.5 rounded">
                            {vat?.rate}%
                          </span>
                          <span className="font-mono text-xs text-foreground">
                            {formatCurrency(vat?.amount, invoice?.currency)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-mono text-sm font-medium text-foreground">
                      {formatCurrency(invoice?.totalAmount, invoice?.currency)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col space-y-1">
                      {invoice?.confidenceCapReason === 'manual_edit' ? (
                        <span className="inline-flex items-center space-x-2 text-sm font-medium text-sky-700 bg-sky-50 border border-sky-200 rounded-full px-3 py-1">
                          <Icon name="Info" size={14} />
                          <span>Manual</span>
                        </span>
                      ) : (
                        <div className="flex items-center space-x-2">
                          <span className={`font-mono text-sm ${getConfidenceColor(invoice?.confidence)}`}>
                            {Math.round(invoice?.confidence * 100)}%
                          </span>
                          <div className="w-12 h-1.5 bg-muted rounded-full overflow-hidden">
                            <div 
                              className={`h-full transition-all duration-300 ${
                                invoice?.confidence >= 0.9 ? 'bg-success' : 
                                invoice?.confidence >= 0.7 ? 'bg-warning' : 'bg-error'
                              }`}
                              style={{ width: `${invoice?.confidence * 100}%` }}
                            />
                          </div>
                        </div>
                      )}
                      {invoice?.confidenceCapReason === 'lines_totals_mismatch' && (
                        <span 
                          className="inline-flex items-center space-x-1 text-xs font-medium text-warning bg-yellow-50 text-yellow-800 rounded-full px-2 py-0.5"
                          title={`Totals do not match line items${invoice?.mismatchDelta ? ` (Δ Net £${(invoice?.mismatchDelta?.net||0).toFixed(2)}, VAT £${(invoice?.mismatchDelta?.vat||0).toFixed(2)}, Gross £${(invoice?.mismatchDelta?.gross||0).toFixed(2)})` : ''}`}
                        >
                          <Icon name="AlertTriangle" size={12} />
                          <span>Totals ≠ Lines</span>
                        </span>
                      )}
                      {invoice?.confidenceCapReason === 'date_invalid_or_missing' && (
                        <span
                          className="inline-flex items-center space-x-1 text-xs font-medium text-warning bg-warning/15 rounded-full px-2 py-0.5"
                          title="Missing or invalid invoice date—confidence capped"
                        >
                          <Icon name="AlertTriangle" size={12} />
                          <span>Invalid/missing date</span>
                        </span>
                      )}
                      {invoice?.confidenceCapReason === 'manual_edit' && (
                        <span className="inline-flex items-center space-x-1 text-xs font-medium text-primary bg-primary/10 rounded-full px-2 py-0.5">
                          <Icon name="PenSquare" size={12} />
                          <span>Manually edited</span>
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center space-x-2">
                      <StatusIndicator 
                        status={statusConfig?.status}
                        variant={statusConfig?.variant}
                        size="sm"
                        confidence={invoice?.confidence}
                      />
                      {isReviewed && (
                        <Icon name="CheckCircle" size={16} className="text-success" />
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3" onClick={(e) => e?.stopPropagation()}>
                    <div className="flex items-center space-x-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Edit"
                        title="Edit invoice"
                        onClick={() => handleRowClick(invoice)}
                        className="h-8 w-8 p-0"
                      />
                      {/* Removed View button (Edit covers this) */}
                      {onDebug && (
                        <Button
                          variant="ghost"
                          size="sm"
                          iconName="Bug"
                          title="Debug AI extraction"
                          onClick={() => onDebug(invoice)}
                          className="h-8 w-8 p-0"
                        />
                      )}
                      {onDownload && (
                        <Button
                          variant="ghost"
                          size="sm"
                          iconName="Download"
                          title="Download PDF"
                          onClick={() => onDownload(invoice)}
                          className="h-8 w-8 p-0"
                        />
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        
        {invoices?.length === 0 && (
          <div className="p-12 text-center">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="FileText" size={32} className="text-muted-foreground" />
            </div>
            <h3 className="text-lg font-medium text-foreground mb-2">No invoices found</h3>
            <p className="text-muted-foreground mb-4">
              Upload some invoices to get started or adjust your filters.
            </p>
            <Button
              variant="outline"
              iconName="Upload"
              iconPosition="left"
              onClick={() => navigate('/uploads')}
            >
              Upload Invoices
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default InvoiceTable;
